/**
 * @file 有效进张（Ukeire）计算 —— `design/gdd/06-ai-strategy.md` §3.4。
 *
 * 有效进张 = 摸到后能让向听数下降的牌种。`total`（进张总枚数）比
 * `tiles.length`（进张种数）重要得多 —— 听两张但都只剩 1 枚，不如听一张
 * 但还剩 4 枚。只有困难档 AI 使用 `total`，普通档用 `tiles.length` 近似。
 *
 * 纯函数、平台无关。`counts` 会被临时 +1/-1 并恢复（观察等价于只读）。
 */

import { suitToIndex } from '../tiles';
import type { Suit } from '../types';
import { shantenSichuan } from './shanten';
import type { TileCounts, TileKey } from './types';

/**
 * 有效进张。
 *
 * @param counts    手牌计数向量（约 13 张）
 * @param meldCount 已副露面子数
 * @param queSuit   定缺花色；`null` 表示不限制
 * @param seen      「已见张数」表（`buildSeenCounts` 产出）；`live = 4 - seen[k]`
 * @returns `tiles` 为有效进张的牌种序号列表；`total` 为进张总枚数（用 `liveCount` 加权）
 */
export function ukeire(
  counts: TileCounts,
  meldCount: number,
  queSuit: Suit | null,
  seen: Uint8Array,
): { readonly tiles: TileKey[]; readonly total: number } {
  const cur = shantenSichuan(counts, meldCount, queSuit);
  const tiles: TileKey[] = [];
  let total = 0;
  for (let k = 0; k < 27; k++) {
    if (queSuit !== null && Math.floor(k / 9) === suitToIndex(queSuit)) continue; // 缺门牌无效
    if ((counts[k] ?? 0) >= 4) continue;
    const live = 4 - (seen[k] ?? 0);
    if (live <= 0) continue; // ★ 已绝张，无效进张
    counts[k] = (counts[k] ?? 0) + 1;
    if (shantenSichuan(counts, meldCount, queSuit) < cur) {
      tiles.push(k);
      total += live;
    }
    counts[k] = (counts[k] ?? 0) - 1;
  }
  return { tiles, total };
}
